import { useState, useEffect, useRef } from 'react';

export const useWebSocket = (sessionId, onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const connect = () => {
    if (!sessionId) return;
    
    // Clear existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Intelligent URL detection
    let apiUrl;
    const isLocalIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(window.location.hostname);
    const isLocalhost = window.location.hostname === 'localhost';

    if (isLocalIP || isLocalhost) {
      apiUrl = `${window.location.protocol}//${window.location.host}`;
    } else {
      apiUrl = 'https://heksta-backend-final.onrender.com';
    }

    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const cleanHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}://${cleanHost}/?sessionId=${sessionId}`;
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (onMessageRef.current) onMessageRef.current(message);

        if (message.type !== 'broadcast_file_chunk' && message.type !== 'private_file_chunk') {
          setMessages(prev => [...prev, message]);
        }

        if (message.type === 'connected') {
          setClientInfo({
            clientId: message.clientId,
            sessionInfo: message.sessionInfo
          });
        }
      } catch (error) {}
    };

    ws.onclose = () => {
      setIsConnected(false);
      handleReconnect();
    };

    ws.onerror = () => {
      setIsConnected(false);
    };
  };

  const handleReconnect = () => {
    if (!sessionId) return;
    if (reconnectTimeoutRef.current) return;

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttempts.current += 1;
      reconnectTimeoutRef.current = null;
      connect();
    }, delay);
  };

  useEffect(() => {
    connect();

    const handleOnline = () => {
      console.log('🌐 Network back online, reconnecting...');
      reconnectAttempts.current = 0;
      connect();
    };

    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [sessionId]);

  const sendMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  return { isConnected, messages, clientInfo, sendMessage };
};